import React, { useMemo } from 'react';
import { Typography, Tag } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import CrudPage from '../../components/common/CrudPage';
import featureRatingConfigService from '../../services/survey/featureRatingConfigService';
import surveyService from '../../services/survey/surveyService';
import { listActiveFeaturesRequest } from '../../services/cms/featureServiceRequest';

const { Text } = Typography;

const FeatureRatingConfigManagement = () => {
  // 1. Lấy danh sách tính năng đang hoạt động từ bảng feature
  const { data: rawFeatures } = useQuery({
    queryKey: ['activeFeaturesForRating'],
    queryFn: () => listActiveFeaturesRequest(),
  });

  // Hiển thị dropdown lấy trường name thay vì display_name theo yêu cầu
  const featureOptions = useMemo(() => {
    const list = Array.isArray(rawFeatures)
      ? rawFeatures
      : (Array.isArray(rawFeatures?.data) ? rawFeatures.data : []);
    return list.map((item) => ({
      label: `[#${item.id}] ${item.name}`,
      value: item.id,
      name: item.name,
    }));
  }, [rawFeatures]);

  // 2. Lấy danh sách bài khảo sát đang hoạt động (tương ứng với menu "Khảo sát (Survey)")
  const { data: rawSurveys } = useQuery({
    queryKey: ['activeSurveysForRating'],
    queryFn: () => surveyService.getList({ page: 1, limit: 500, status: 1 }),
  });

  const surveyOptions = useMemo(() => {
    const list = Array.isArray(rawSurveys?.data)
      ? rawSurveys.data
      : (Array.isArray(rawSurveys) ? rawSurveys : []);
    return list.map((item) => ({
      label: `[#${item.id}] ${item.title}`,
      value: item.id,
      title: item.title,
    }));
  }, [rawSurveys]);

  const filterFields = useMemo(
    () => [
      {
        label: 'Trạng thái',
        value: 'status',
        options: [
          { label: 'Tất cả trạng thái', value: '' },
          { label: 'Hoạt động', value: '1' },
          { label: 'Tạm khóa', value: '0' },
        ],
      },
      {
        label: 'Khảo sát nhiều lần',
        value: 'allow_multiple_survey',
        options: [
          { label: 'Tất cả', value: '' },
          { label: 'Cho phép nhiều lần', value: '1' },
          { label: 'Chỉ 1 lần', value: '0' },
        ],
      },
    ],
    []
  );

  const formFields = useMemo(
    () => [
      { type: 'divider', label: 'Thông tin tính năng' },
      {
        name: 'feature_id',
        label: 'Tính năng liên kết',
        type: 'select',
        rules: [{ required: true, message: 'Vui lòng chọn tính năng!' }],
        placeholder: 'Chọn tính năng từ danh mục...',
        propsInput: {
          showSearch: true,
          allowClear: true,
          optionFilterProp: 'label',
        },
        options: featureOptions,
        md: 12,
      },
      {
        name: 'feature_name',
        label: 'Tên hiển thị tính năng',
        type: 'input',
        placeholder: 'Để trống sẽ tự động lấy tên tính năng đã chọn (trường name)',
        md: 12,
      },
      {
        name: 'description',
        label: 'Mô tả tính năng',
        type: 'textarea',
        rows: 2,
        placeholder: 'Nhập mô tả tính năng...',
      },
      {
        name: 'status',
        label: 'Trạng thái hoạt động',
        type: 'select',
        options: [
          { label: 'Hoạt động', value: 1 },
          { label: 'Tạm khóa', value: 0 },
        ],
        defaultValue: 1,
        md: 12,
      },

      { type: 'divider', label: 'Cấu hình bài khảo sát (Survey kèm theo)' },
      {
        name: 'survey_id_helpful',
        label: 'Khảo sát khi Hữu ích (Thumbs Up 👍)',
        type: 'select',
        placeholder: 'Chọn bài khảo sát khi user vote Hữu ích...',
        propsInput: {
          showSearch: true,
          allowClear: true,
          optionFilterProp: 'label',
        },
        options: surveyOptions,
        md: 12,
      },
      {
        name: 'survey_id_unhelpful',
        label: 'Khảo sát khi Không hữu ích (Thumbs Down 👎)',
        type: 'select',
        placeholder: 'Chọn bài khảo sát khi user vote Không hữu ích...',
        propsInput: {
          showSearch: true,
          allowClear: true,
          optionFilterProp: 'label',
        },
        options: surveyOptions,
        md: 12,
      },
      {
        name: 'allow_multiple_survey',
        type: 'checkbox',
        labelCheckbox: 'Cho phép làm khảo sát nhiều lần (user đã nộp vẫn tiếp tục được hiển thị khảo sát)',
        defaultChecked: true,
        md: 24,
      },
    ],
    [featureOptions, surveyOptions]
  );

  const mapFormToData = (values) => {
    let featureName = values.feature_name?.trim();
    if (!featureName && values.feature_id) {
      const matched = featureOptions.find((f) => f.value === Number(values.feature_id));
      featureName = matched ? matched.name : '';
    }
    return {
      ...values,
      feature_id: Number(values.feature_id),
      feature_name: featureName || (values.feature_id ? `Tính năng #${values.feature_id}` : ''),
      survey_id_helpful: values.survey_id_helpful ? Number(values.survey_id_helpful) : null,
      survey_id_unhelpful: values.survey_id_unhelpful ? Number(values.survey_id_unhelpful) : null,
      status: values.status !== undefined && values.status !== null ? Number(values.status) : 1,
      allow_multiple_survey: values.allow_multiple_survey ? 1 : 0,
    };
  };

  const mapDataToForm = (record) => ({
    allow_multiple_survey: Number(record.allow_multiple_survey) === 1,
  });

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 70,
        align: 'center',
      },
      {
        title: 'ID TÍNH NĂNG',
        dataIndex: 'feature_id',
        key: 'feature_id',
        width: 140,
        align: 'center',
        render: (fId) => <Tag color="blue" className="font-mono text-sm font-semibold">#{fId}</Tag>,
      },
      {
        title: 'TÊN TÍNH NĂNG',
        dataIndex: 'feature_name',
        key: 'feature_name',
        render: (name, record) => (
          <div>
            <Text strong>{name}</Text>
            {record.description && (
              <div className="text-xs text-gray-400 mt-1 line-clamp-1">{record.description}</div>
            )}
          </div>
        ),
      },
      {
        title: 'SURVEY HỮU ÍCH (👍)',
        dataIndex: 'survey_id_helpful',
        key: 'survey_id_helpful',
        width: 230,
        render: (id, record) => {
          if (!id) return <Text type="secondary">—</Text>;
          const title = record.survey_helpful?.title || `Survey #${id}`;
          return (
            <Tag color="green" className="text-xs">
              👍 [#{id}] {title}
            </Tag>
          );
        },
      },
      {
        title: 'SURVEY KHÔNG HỮU ÍCH (👎)',
        dataIndex: 'survey_id_unhelpful',
        key: 'survey_id_unhelpful',
        width: 230,
        render: (id, record) => {
          if (!id) return <Text type="secondary">—</Text>;
          const title = record.survey_unhelpful?.title || `Survey #${id}`;
          return (
            <Tag color="volcano" className="text-xs">
              👎 [#{id}] {title}
            </Tag>
          );
        },
      },
      {
        title: 'TRẠNG THÁI',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        align: 'center',
        render: (status) => (
          <Tag color={status === 1 ? 'green' : 'red'}>
            {status === 1 ? 'HOẠT ĐỘNG' : 'TẠM KHÓA'}
          </Tag>
        ),
      },
      {
        title: 'KS NHIỀU LẦN',
        dataIndex: 'allow_multiple_survey',
        key: 'allow_multiple_survey',
        width: 130,
        align: 'center',
        render: (value) => (
          <Tag color={Number(value) === 1 ? 'blue' : 'default'}>
            {Number(value) === 1 ? 'CÓ' : 'KHÔNG'}
          </Tag>
        ),
      },
      {
        title: 'CẬP NHẬT',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 160,
        render: (date) => (date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'),
      },
    ],
    [featureOptions]
  );

  return (
    <CrudPage
      title="KHẢO SÁT TÍNH NĂNG HỮU ÍCH"
      subTitle="Cấu hình khảo sát hữu ích / không hữu ích cho từng tính năng trong hệ thống"
      itemsName="Cấu hình tính năng"
      queryKey="featureRatingConfigList"
      service={featureRatingConfigService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      mapFormToData={mapFormToData}
      searchPlaceholder="Tìm kiếm tên tính năng..."
      addButtonLabel="Thêm cấu hình"
      formConfig={{ width: 850 }}
    />
  );
};

export default FeatureRatingConfigManagement;
